import { EditorContent, type EditorEvents } from '@tiptap/react';
import React, { MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';
import type { CellData, CellEditingSession } from '../../../../common/table/TableTypes';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import getTiptapNodeTextContent from '../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';
import useUpdatedRef from '../../../utils/react/useUpdatedRef';
import { MNHotInstance } from '../tableHotInstanceTypes';
import { getTableCellEditorId } from '../utils/tableEditorSessionUtils';
import {
    getTiptapCellContent,
    getTiptapCellOverwriteContent,
    getTiptapContentFromPlainText,
} from './tiptapCellEditorContentUtils';
import { initialiseTiptapCellEditorContent } from './tiptapCellEditorInitialisationUtils';
import useTableCellTextAlignment from './useTableCellTextAlignment';
import { useTableCellEditorTiptapExtensions } from './useTableCellTiptapExtensions';

type Props = {
    element: ImMNElement;
    elementId: string;
    isEditable: boolean;
    cellData: CellData | undefined;
    cellEditingSession: CellEditingSession | null;
    currentlyEditingEditorId: string | null;
    hotTableInstanceRef: MutableRefObject<MNHotInstance<TiptapContent, TiptapContent> | null>;
    cellEditorRef: MutableRefObject<MilanoteEditorRef<TiptapContent> | null>;
    locale: string;
};

const TiptapCellEditorComponent: React.FC<Props> = ({
    elementId,
    isEditable,
    cellData,
    cellEditingSession,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    locale,
    cellEditorRef,
}) => {
    const { textAlignment, recomputeEditingTextAlignment } = useTableCellTextAlignment({
        cellData,
        cellEditingSession,
        locale,
    });

    const tiptapEditorRef = useRef<HTMLDivElement | null>(null);
    const cellEditingSessionRef = useUpdatedRef(cellEditingSession);
    const recomputeEditingTextAlignmentRef = useUpdatedRef(recomputeEditingTextAlignment);

    const saveContent = useCallback(
        (content: TiptapContent) => {
            if (!cellEditingSessionRef.current) return;

            recomputeEditingTextAlignmentRef.current(getTiptapNodeTextContent(content));

            const activeEditor = hotTableInstanceRef.current?.getMNEditor();
            activeEditor?.onMilanoteCellEditorChange(content);

            // @ts-ignore - Custom Milanote hook
            hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', content);
        },
        [hotTableInstanceRef],
    );

    const extensions = useTableCellEditorTiptapExtensions({
        isEditable,
        saveContent,
        hotTableInstanceRef,
        cellEditingSessionRef,
    });
    const editorId = getTableCellEditorId(elementId);

    const persistedContent = useMemo(
        () => (cellEditingSession ? getTiptapCellContent({ cellData, locale }) : null),
        [cellData, cellEditingSession, locale],
    );
    const overwriteContent = useMemo(
        () =>
            getTiptapCellOverwriteContent({
                cellData,
                overwriteContent: cellEditingSession?.overwriteContent,
            }),
        [cellData, cellEditingSession?.overwriteContent],
    );

    const { editor } = useClientElementTiptapEditor({
        persistedContent,
        // Cell content is owned by this edit session, so a deferred sync can overwrite the initial typed character.
        shouldSyncPersistedContent: false,
        extensions,

        editorId,
        editorKey: TABLE_CELL_EDITOR_KEY,
        currentEditorId: currentlyEditingEditorId || undefined,

        isEditable,
        isEditing: !!cellEditingSession,
        isSingleSelected: true,
    });

    useLayoutEffect(() => {
        if (!cellEditingSession || !editor || !persistedContent) return;

        initialiseTiptapCellEditorContent({ editor, persistedContent, overwriteContent });

        // Keep plugin hooks in sync with the initial editor content when edit mode opens.
        // (eg formula reference highlighting would otherwise wait for the first edit rather than showing immediately)
        // @ts-ignore - Custom Milanote hook
        hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', editor.getJSON());
    }, [cellEditingSession, editor, hotTableInstanceRef, overwriteContent, persistedContent]);

    useEffect(() => {
        if (!cellEditingSession || !editor) return;

        const onSelectionOnlyTransaction = ({ transaction }: EditorEvents['transaction']) => {
            if (!transaction.selectionSet || transaction.docChanged) return;

            // @ts-ignore - Custom Milanote hook
            hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorSelectionChange');
        };

        editor.on('transaction', onSelectionOnlyTransaction);

        return () => {
            editor.off('transaction', onSelectionOnlyTransaction);
        };
    }, [cellEditingSession, editor, hotTableInstanceRef]);

    useEffect(() => {
        // TODO-TABLES: This can be refactored to be less hacky after draft is removed
        const editorWrapper: MilanoteEditorRef<TiptapContent> = {
            getEditor: () => editor,
            getEditorState: () => (editor ? (editor.getJSON() as any) : null),
            onChange: (content) => editor?.commands.setContent(content as any),
            setPlainText: (text: string, inlineStyles?: string[]) =>
                editor?.commands.setContent(getTiptapContentFromPlainText(text, inlineStyles)),
        };
        cellEditorRef.current = editorWrapper;

        return () => {
            if (cellEditorRef.current === editorWrapper) cellEditorRef.current = null;
        };
    }, [cellEditorRef, editor]);

    useEffect(() => {
        // This lets handsontable know which element is the active editor
        tiptapEditorRef.current!.setAttribute('data-hot-input', 'true');
    }, []);

    return (
        <div className="handsontable-input TableTiptapCellEditor" style={{ textAlign: textAlignment || undefined }}>
            <EditorContent
                innerRef={tiptapEditorRef}
                className="TableTiptapCellRenderer-content"
                dir="auto"
                editor={editor}
            />
        </div>
    );
};

export default TiptapCellEditorComponent;
